import React, {useState, useMemo} from 'react';
import {Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, Box, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TextField} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import DialogUploadFile, {ExcelHeader} from '../../../../../ui-component/dialogs/uploadFileDialog';
import {findClosestMatchIndex} from "../../../all-data/utils";
import {roundDecimals} from "../../../../../fromKotlin/nkutils";
import {read, utils} from "xlsx";
import extractProductsFromText from "../../../../../utils/openaiUtils";
import {
    extractProductsFromPdfAssistant,
    extractProductsWithAssistant2
} from "../../../../../utils/openaiProductAssistant";
import {toArrayOrEmpty} from "../../../../../types/DocumentsTypes/utils";
import JSDB from "../../../../../fromKotlin/nk";

// You may need to import PDF/text parsing utilities here

// Define a type for the loaded product rows
export type LoadedProductRow = {
    id?: string | number;
    name?: string;
    quantity?: number;
    matchedProduct?: any; // Will be matched to user's products
    [key: string]: any;
};

interface LoadProductsDialogProps {
    open: boolean;
    onClose: () => void;
    onDone: (products: LoadedProductRow[]) => void;
    userProducts: any[]; // List of products to match against
}

const excelHeaders: ExcelHeader[] = [
    {
        name: 'מוצר',
        must: true,
        type: 'string',
        picker: ['id', 'external_id', 'category', 'group'],
        defultValue: 'id'
    },
    {
        name: 'כמות',
        must: true,
        type: 'number'

    },
    {
        name: 'מחיר',
        must: false,
        type: 'number'
    },
    {
        name: 'הערות',
        must: false,
        type: 'string'
    }
];

const LoadProductsDialog: React.FC<LoadProductsDialogProps> = ({open, onClose, onDone, userProducts}) => {
    const [tab, setTab] = useState(0);
    const [loadedRows, setLoadedRows] = useState<LoadedProductRow[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [textInput, setTextInput] = useState("");
    const [isLoading, setIsLoading] = useState(false); // Add loading state

    // Handler for Excel upload
    const handleExcelUpload = async (file: File, args: any) => {
        if (!file) return;

        const productMap = {};
        const products = userProducts;
        const productNames = userProducts.map((p) => p.name);
        console.log(products);
        products.forEach(product => {
            productMap[product[args.productPicker ?? 'id']] = product;
        });
        console.log(productMap);
        const readTextWithEncoding = (file: File, encoding = 'windows-1255'): Promise<string> => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsText(file, encoding);
            });
        };

        const text = await readTextWithEncoding(file, 'windows-1255');

        let wb;

        if (text.startsWith('<HTML>') || text.includes('<TABLE')) {
            wb = read(text, {type: 'string'});
        } else {
            const buffer = await file.arrayBuffer();
            wb = read(buffer, {type: 'array'});
        }

        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = utils.sheet_to_json(ws, {header: 1, raw: true, defval: ""});

        if (!data || data.length === 0) return [];

        const cols = args.cols;
        const th = 0.37;

        const results: LoadedProductRow[] = data.slice(args.skip_first_line ? 2 : 1).map((row: any) => {
            const p = row[cols["מוצר"] ?? 0];
            const quantity = row[cols["כמות"] ?? 1];
            const pname = row[cols["שם מוצר"] ?? 2];
            const notes = cols["הערות"] ? row[cols["הערות"] ?? 3]:undefined ;
            console.log(p, quantity, pname,notes);
            let product = productMap[p];
            console.log(product)
            let findName = false;
            let matchScore = 1;

            if (!product && pname) {
                const score = findClosestMatchIndex(pname, productNames);
                const ind = score.index;
                matchScore = roundDecimals(score.matchScore, 2);
                if (ind !== -1) {
                    findName = true;
                    product = products[ind];
                }
            }

            if (quantity && product) {
                return {
                    id: product.id,
                    name: findName ? `${pname} = ${product.name}` : product.name,
                    quantity: parseFloat(quantity),
                    matchedProduct: product,
                    score: matchScore,
                    notes:notes,
                    status: 1
                };
            } else {
                return {
                    id: p,
                    name: pname,
                    quantity: parseFloat(quantity),
                    score: matchScore
                };
            }
        }).filter((row) => row);
        console.log(results)
        setLoadedRows(results);
        return results.map((row, index) => {
            return {
                line: index + 1,
                status: row.status,
                score: row.score
            }
        });
    };

    // Handler for text upload
    const handleTextUpload = async (text: string) => {
        setIsLoading(true); // Start loading

        try {
            const x = await extractProductsWithAssistant2(text, "sk-_jPzHza7rACKR6NTA9VRu-TYRpxwfu2XTRVoNVnQv8T3BlbkFJC1tqWRUg0serLU-0BTa-dAqAd4zdcT7mzbFW0qsoMA", toArrayOrEmpty(JSDB().getAllClientProduct(true).first).map((p) => {
                return {
                    id: p.id,
                    name: p.name,
                    nicknames : [p.getOrderProduct()?.orderName??''],
                };
            }));
            console.log(x);
            const productMap = {};
            userProducts.forEach(product => {
                productMap[product.id] = product;
            });
            console.log(productMap);
            const loadRows = x.map((row) => {
                const product = productMap[row.id];
                let quantity = row.quantity;
                let quantitySecond = undefined;

                if (product && product.available_units?.length === 2 && row.unit && row.unit!='') {
                    const unitIndex = product.available_units.indexOf(row.unit);
                    if (unitIndex === 1) {
                        quantitySecond = true;

                    }
                }

                return {
                    id: row.id,
                    name: row.user_product_text,
                    quantity: quantity,
                    sec:quantitySecond,
                    matchedProduct: product,
                    score: row.score
                };
            });

            setLoadedRows(loadRows);
        } catch (error) {
            console.error("Error uploading text:", error);
        } finally {
            setIsLoading(false); // Stop loading
        }
    };

    // Handler for PDF upload
    const handlePdfUpload = async (file: File) => {
        // Extract text, then parse as above
        // setLoadedRows(parsedRows);
        setIsLoading(true);
        try {
            // Call your OpenAI-based PDF extractor

            const pdfExtracted = await extractProductsFromPdfAssistant(file, "sk-_jPzHza7rACKR6NTA9VRu-TYRpxwfu2XTRVoNVnQv8T3BlbkFJC1tqWRUg0serLU-0BTa-dAqAd4zdcT7mzbFW0qsoMA", toArrayOrEmpty(JSDB().getAllClientProduct(true).first).map((p) => {
                return {
                    id: p.id,
                    name: p.name,
                    nicknames : [p.getOrderProduct()?.orderName??''],
                };
            }));

            console.log("PDF assistant extracted:", pdfExtracted);

            // Optionally: create a map for product matching
            const productMap = {};
            userProducts.forEach(product => {
                productMap[product.id] = product;
            });

            const loadRows = x.map((row) => {
                const product = productMap[row.id];
                let quantity = row.quantity;
                let quantitySecond = undefined;

                if (product && product.available_units?.length === 2 && row.unit && row.unit!='') {
                    const unitIndex = product.available_units.indexOf(row.unit);
                    if (unitIndex === 1) {
                        quantitySecond = true;

                    }
                }

                return {
                    id: row.id,
                    name: row.user_product_text,
                    quantity: quantity,
                    sec:quantitySecond,
                    matchedProduct: product,
                    score: row.score
                };
            });

            setLoadedRows(loadRows);
        } catch (error) {
            console.error("Error uploading text:", error);
        } finally {
            setIsLoading(false); // Stop loading
        }
    };

    // Same product id appearing more than once usually means the same row was pasted twice, or the
    // file mixes size/unit variants under one id - flag it so the quantities aren't silently doubled.
    const duplicateProductIds = useMemo(() => {
        const counts: Record<string, number> = {};
        loadedRows.forEach((row) => {
            if (row.id == null || row.id === '') return;
            const key = String(row.id);
            counts[key] = (counts[key] || 0) + 1;
        });
        return Object.keys(counts).filter((key) => counts[key] > 1);
    }, [loadedRows]);

    const matchedCount = loadedRows.filter((r) => r.matchedProduct).length;
    const unmatchedCount = loadedRows.length - matchedCount;

    // Matching UI: let user validate/edit matched products
    const renderMatchTable = () => (
        <Box>
            <Typography variant="h6">מוצרים תואמים</Typography>
            <Alert severity={unmatchedCount > 0 ? 'warning' : 'success'} sx={{ mb: 1 }}>
                {matchedCount} מתוך {loadedRows.length} שורות הותאמו למוצר קיים.
                {unmatchedCount > 0 ? ` ${unmatchedCount} שורות מסומנות באדום דורשות התאמה ידנית או הסרה לפני הסיום.` : ''}
            </Alert>
            {duplicateProductIds.length > 0 ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    שימו לב: מוצר/י מספר {duplicateProductIds.join(', ')} מופיע/ים יותר משורה אחת. בדקו שאין כפילות בטעות לפני הסיום.
                </Alert>
            ) : null}
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>מזהה מוצר</TableCell>
                            <TableCell>שם מוצר</TableCell>
                            <TableCell>מוצר נטען</TableCell>
                            <TableCell style={{ width: '100px' }}>כמות</TableCell>
                            <TableCell>ציון</TableCell>
                            <TableCell>פעולות</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadedRows.map((row, index) => {
                            let backgroundColor = "";
                            if (row.score < 0.6) {
                                backgroundColor = "#fff3cd"; // Light yellow
                            } else if (row.score > 0.8) {
                                backgroundColor = "#d4edda"; // Light green
                            } else if (!row.matchedProduct) {
                                backgroundColor = "#f8d7da"; // Light red
                            }

                            return (
                                <TableRow key={index} style={{ backgroundColor }}>
                                    <TableCell>{row.id}</TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>
                                        <Autocomplete
                                            options={userProducts}
                                            getOptionLabel={(option) => option.name}
                                            value={row.matchedProduct || null}
                                            onChange={(event, newValue) => {
                                                const updatedRows = [...loadedRows];
                                                updatedRows[index].matchedProduct = newValue;
                                                updatedRows[index].id = newValue?.id;
                                                setLoadedRows(updatedRows);
                                            }}
                                            renderInput={(params) => <TextField {...params} label="בחר מוצר" />}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            value={row.quantity || ''}
                                            onChange={(event) => {
                                                const updatedRows = [...loadedRows];
                                                updatedRows[index].quantity = parseFloat(event.target.value) || 0;
                                                setLoadedRows(updatedRows);
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>{row.score}</TableCell>
                                    <TableCell>
                                        <Button
                                            color="secondary"
                                            onClick={() => {
                                                const updatedRows = loadedRows.filter((_, i) => i !== index);
                                                setLoadedRows(updatedRows);
                                            }}
                                        >
                                            הסר
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>טען מוצרים מקובץ</DialogTitle>
            <DialogContent>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label="אקסל"/>
                    <Tab label="טקסט"/>
                </Tabs>
                <Box hidden={tab !== 0}>
                    <Alert severity="info" sx={{ mb: 1 }}>
                        הקובץ צריך לכלול עמודת מזהה מוצר (או קטגוריה/בר-קוד לפי הבחירה) ועמודת כמות. אחרי הטעינה ניתן לתקן התאמות, לערוך כמויות ולהסיר שורות לפני הסיום.
                    </Alert>
                    <DialogUploadFile
                        uploadCallback={handleExcelUpload}
                        title={'טען מוצרים מאקסל'}
                        body={excelHeaders}
                        showPickerItems
                        excelHeaders={
                            [{
                                name: 'productPicker',
                                must: true,
                                type: 'string',
                                picker: ['category', 'category2', 'id', 'external_id','barcode']
                            },]
                        }
                        finishCallBack={(parsedRows) => {
                            // Handle parsed rows, e.g., update state or display in UI
                        }}
                    />
                </Box>
                <Box hidden={tab !== 1}>
                    <TextField
                        multiline
                        fullWidth
                        rows={6}
                        placeholder=" עבניות 55 קילו דוגמא: 3 פיתות 4 לחמים, 15 כוסמין,"
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleTextUpload(textInput)}
                        style={{ marginTop: "10px" }}
                        disabled={isLoading} // Disable button while loading
                    >
                        {isLoading ? "טוען..." : "החל"} {/* Show spinner or text */}
                    </Button>
                </Box>
                {loadedRows.length > 0 && renderMatchTable()}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>ביטול</Button>
                <Button onClick={() => onDone(loadedRows)} color="primary" variant="contained"
                        disabled={loadedRows.length === 0}>סיום</Button>
            </DialogActions>
        </Dialog>
    );
};

export default LoadProductsDialog;

